/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import type { ReactElement } from 'react';
import React from 'react';
import { List } from 'react-window';
import type { RowComponentProps } from 'react-window';
import type { ServiceInterface } from '@opengeoweb/shared';
import type { layerTypes, serviceTypes } from '@opengeoweb/store';
import { CollectionListRow } from './CollectionListRow';
import type { DeleteLayerParams } from './LayerAddRemoveButton';
import { LayerListRow } from './LayerListRow';
import type { LayerWithServiceName } from '../../store/types';
import { useLayerSelectTranslation } from '../../utils/i18n';
import { widthToRowHeight } from '../LayerSelect/LayerSelectUtils';

interface AddLayerParams {
  serviceUrl: string;
  layerName: string;
  serviceType?: ServiceInterface;
}

type DisplayRow =
  | {
      type: 'collection';
      key: string;
      title: string;
      count: number;
      level: number;
      expanded: boolean;
      onToggle: () => void;
    }
  | { type: 'layer'; layer: LayerWithServiceName };

interface TreeNode {
  key: string;
  title: string;
  level: number;
  children: Map<string, TreeNode>;
  layers: LayerWithServiceName[];
}

const countTreeLayers = (node: TreeNode): number =>
  node.layers.length +
  Array.from(node.children.values()).reduce(
    (count, child) => count + countTreeLayers(child),
    0,
  );

export interface LayerListProps {
  services: serviceTypes.Services;
  filteredLayers: LayerWithServiceName[];
  layerSelectHeight: number;
  serviceListHeight: number;
  layerSelectWidth: number;
  allLayers: layerTypes.ReduxLayer[];
  allMapIds: string[];
  addLayer: ({ serviceUrl, layerName }: AddLayerParams) => void;
  addLayerToAllMaps: ({ serviceUrl, layerName }: AddLayerParams) => void;
  deleteLayer: ({ layerId }: DeleteLayerParams) => void;
  removeLayerFromAllMaps: (layerId: string) => void;
  mapLayers: layerTypes.ReduxLayer[];
  searchFilter: string;
  isLayerInfoVisibleInStore: boolean;
  onToggleActiveLayer: (
    params: AddLayerParams & { isActiveLayer: boolean },
  ) => void;
  currentLayerInfo?: AddLayerParams;
  expandEdrGroupsByDefault?: boolean;
}

type LayerRowExtraProps = Pick<
  LayerListProps,
  | 'services'
  | 'filteredLayers'
  | 'allLayers'
  | 'allMapIds'
  | 'addLayer'
  | 'addLayerToAllMaps'
  | 'deleteLayer'
  | 'removeLayerFromAllMaps'
  | 'mapLayers'
  | 'searchFilter'
  | 'isLayerInfoVisibleInStore'
  | 'onToggleActiveLayer'
  | 'currentLayerInfo'
  | 'layerSelectWidth'
> & {
  rowMargin: number;
  displayRows: DisplayRow[];
};

const LayerRow = ({
  index,
  style,
  services,
  displayRows,
  allLayers,
  allMapIds,
  addLayer,
  addLayerToAllMaps,
  deleteLayer,
  removeLayerFromAllMaps,
  mapLayers,
  searchFilter,
  isLayerInfoVisibleInStore,
  onToggleActiveLayer,
  currentLayerInfo,
  layerSelectWidth,
  rowMargin,
}: RowComponentProps<LayerRowExtraProps>): ReactElement | null => {
  const displayRow = displayRows[index];
  if (displayRow.type === 'collection') {
    return (
      <div style={style}>
        <CollectionListRow
          count={displayRow.count}
          expanded={displayRow.expanded}
          level={displayRow.level}
          onToggle={displayRow.onToggle}
          title={displayRow.title}
        />
      </div>
    );
  }

  const { layer } = displayRow;
  const service = services[layer.serviceName];

  if (!service) {
    return <div style={style} />;
  }

  const isActiveLayer =
    isLayerInfoVisibleInStore &&
    currentLayerInfo?.serviceUrl === service.serviceUrl &&
    currentLayerInfo?.layerName === layer.name;

  const rowStyle: React.CSSProperties = {
    ...style,
    boxSizing: 'border-box',
    paddingBottom: rowMargin,
  };

  return (
    <div key={layer.name} style={rowStyle}>
      <LayerListRow
        addLayer={addLayer}
        addLayerToAllMaps={addLayerToAllMaps}
        allLayers={allLayers}
        allMapIds={allMapIds}
        deleteLayer={deleteLayer}
        hasActiveLayer={isActiveLayer}
        layer={layer}
        layerSelectWidth={layerSelectWidth}
        mapLayers={mapLayers}
        onToggleActiveLayer={() =>
          onToggleActiveLayer({
            serviceUrl: service.serviceUrl!,
            layerName: layer.name!,
            isActiveLayer,
          })
        }
        removeLayerFromAllMaps={removeLayerFromAllMaps}
        searchFilter={searchFilter}
        service={service}
      />
    </div>
  );
};

export const LayerList: React.FC<LayerListProps> = ({
  services,
  filteredLayers,
  layerSelectHeight,
  serviceListHeight,
  layerSelectWidth,
  allLayers,
  allMapIds,
  addLayer,
  addLayerToAllMaps,
  deleteLayer,
  removeLayerFromAllMaps,
  mapLayers,
  searchFilter,
  isLayerInfoVisibleInStore,
  onToggleActiveLayer,
  currentLayerInfo,
  expandEdrGroupsByDefault = false,
}: LayerListProps) => {
  const { t } = useLayerSelectTranslation();
  const [expandedCollections, setExpandedCollections] = React.useState<
    Record<string, boolean>
  >({});

  const displayRows = React.useMemo<DisplayRow[]>(() => {
    const rows: DisplayRow[] = [];
    const roots = new Map<string, TreeNode>();

    filteredLayers.forEach((layer) => {
      const service = services[layer.serviceName];
      const isEDR = service?.type === 'EDR';
      if (!isEDR || layer.path.length === 0) {
        rows.push({ type: 'layer', layer });
        return;
      }

      let parent = roots.get(`${layer.serviceName}:${layer.path[0]}`);
      layer.path.forEach((title, level) => {
        const key = `${layer.serviceName}:${layer.path
          .slice(0, level + 1)
          .join('/')}`;
        const existing =
          level === 0 ? roots.get(key) : parent?.children.get(key);
        const node =
          existing ??
          ({
            key,
            title,
            level,
            children: new Map<string, TreeNode>(),
            layers: [],
          } satisfies TreeNode);
        if (!existing) {
          if (parent) {
            parent.children.set(key, node);
          } else {
            roots.set(key, node);
          }
        }
        parent = node;
      });
      parent?.layers.push(layer);
    });

    const flatten = (node: TreeNode): void => {
      const expanded =
        expandedCollections[node.key] ?? expandEdrGroupsByDefault;
      rows.push({
        type: 'collection',
        key: node.key,
        title: node.title,
        count: countTreeLayers(node),
        level: node.level,
        expanded,
        onToggle: () =>
          setExpandedCollections((current) => ({
            ...current,
            [node.key]: !expanded,
          })),
      });
      if (!expanded) {
        return;
      }
      node.children.forEach(flatten);
      node.layers.forEach((layer) => rows.push({ type: 'layer', layer }));
    };

    roots.forEach(flatten);
    return rows;
  }, [expandEdrGroupsByDefault, expandedCollections, filteredLayers, services]);

  const rowMargin = 4;
  const rowHeight = widthToRowHeight(layerSelectWidth);

  const layerSelectFilterHeight = 155 + serviceListHeight;
  const minHeight = 320;

  const listHeight =
    layerSelectHeight - layerSelectFilterHeight < minHeight
      ? minHeight
      : layerSelectHeight - layerSelectFilterHeight;

  return (
    <Box data-testid="layerList">
      <Box sx={{ marginBottom: '4px', fontSize: '12px' }}>
        {filteredLayers.length} {t('layer-list-results')}
      </Box>

      <List
        defaultHeight={minHeight}
        rowComponent={LayerRow}
        rowCount={displayRows.length}
        rowHeight={(index) =>
          displayRows[index].type === 'collection' ? 48 : rowHeight + rowMargin
        }
        rowProps={{
          services,
          filteredLayers,
          displayRows,
          allLayers,
          allMapIds,
          addLayer,
          addLayerToAllMaps,
          deleteLayer,
          removeLayerFromAllMaps,
          mapLayers,
          searchFilter,
          isLayerInfoVisibleInStore,
          onToggleActiveLayer,
          currentLayerInfo,
          layerSelectWidth,
          rowMargin,
        }}
        style={{ height: listHeight, width: '100%' }}
      />
    </Box>
  );
};
